/**
 * Klantkaart v10 (motor 7, golf 2 sessie E): de twee tab-routes en hun
 * typen. Los van `../../api.ts` zodat de werkplek-sessies elkaars
 * bestanden niet raken. Bron: `app/schemas/klantkaart_tabs.py`; het
 * klantlogboek (sessie 162) uit `app/schemas/klantlogboek.py`.
 */
import { apiClient } from "@/lib/api";

export interface KaartStap {
  sleutel: string;
  label: string;
  gedaan: boolean;
  /** Leeg bij een gezette stap: het moment bewaren we niet (geen verzonnen datum). */
  op: string | null;
}

export interface KaartPakket {
  volgnummer: number;
  van: number;
  vervoerder: string | null;
  track_trace_code: string | null;
  track_trace_url: string | null;
  status: string | null;
  verzonden_op: string | null;
  bezorgd_op: string | null;
  /**
   * Sessie 177: bron en tijd van de stand. `bevestigd`: de laatste vraag bij
   * de vervoerder gaf antwoord (op `live_op`); `niet_bevestigd`: mislukt of
   * nooit gevraagd, de stand is van `stand_van`; `ophalen`: de vraag loopt.
   */
  live?: "bevestigd" | "niet_bevestigd" | "ophalen";
  live_op?: string | null;
  stand_van?: string | null;
  /** Afleverplek in gewone woorden (buren, brievenbus, voordeur, afhaalpunt). */
  plek?: string | null;
  /** Alleen gevuld als de vervoerder het meldt. */
  handtekening?: boolean | null;
  /** Wat de tabel zei voordat de vervoerder iets anders meldde: dan "onzeker". */
  vorige_status?: string | null;
  vorige_bezorgd_op?: string | null;
}

/** De pakketten van één bestelling bij het openen ervan (sessie 177). */
export interface KaartPakketten {
  order_id: string;
  pakketten: KaartPakket[];
  pakketten_reden: string | null;
  /** De vraag bij de vervoerder loopt: kort daarna opnieuw vragen. */
  ophalen: boolean;
}

/** TAP-tickets 28-10: een vervoerderticket bij Cenfil voor een bestelling. */
export interface KaartTicket {
  ticket_nummer: string | null;
  /** "open", "carrier_forward", "closed" of een onbekende waarde. */
  status: string | null;
  vervoerder_reactie_op: string | null;
  url: string;
}

export interface KaartOrderTab {
  /** Sessie 177: voor de route met de pakketten van deze bestelling. */
  order_id?: string | null;
  ordernummer: string;
  lightspeed_url: string | null;
  /** Werkplek 28-10: de bestelling in Picqer; ontbreekt of null = geen knop. */
  picqer_url?: string | null;
  /** TAP-tickets 28-10: tickets bij Cenfil, nieuwste eerst; ontbreekt of leeg = geen regel. */
  tickets?: KaartTicket[];
  besteld_op: string | null;
  bedrag_cents: number;
  betaalmethode: string | null;
  producten: string[];
  status_label: string | null;
  geannuleerd: boolean;
  stappen: KaartStap[];
  pakketten: KaartPakket[];
  pakketten_reden: string | null;
}

export interface KaartBestellingen {
  orders: KaartOrderTab[];
  heeft_meer: boolean;
}

export interface KaartRetourTab {
  retournummer: string;
  /** Sessie 141-E: voor de link naar het retourdossier (`/retouren?retour=<id>`). */
  retour_id?: string | null;
  ordernummer: string | null;
  /** Sessie 141-E: de order in Lightspeed, zoals in de tab Bestelling. */
  order_lightspeed_url?: string | null;
  aangevraagd_op: string | null;
  reden: string | null;
  status: string;
  stappen: KaartStap[];
  pakket: KaartPakket | null;
  waarde_cents: number;
  producten: string[];
  kosten_voor: "klant" | "voeronline" | "onbekend";
  kosten_tekst: string;
  afloop: string;
}

export interface KaartRetouren {
  retouren: KaartRetourTab[];
  aantal: number;
  aantal_bestellingen: number;
}

export const kaart10Sleutels = {
  bestellingen: (klantId: string) => ["v6", "klantkaart", klantId, "bestellingen"] as const,
  pakketten: (klantId: string, orderId: string) =>
    ["v6", "klantkaart", klantId, "pakketten", orderId] as const,
  retouren: (klantId: string) => ["v6", "klantkaart", klantId, "retouren"] as const,
  gesprekken: (klantId: string) => ["v6", "klantkaart", klantId, "gesprekken"] as const,
  logboek: (klantId: string) => ["v6", "klantkaart", klantId, "logboek"] as const,
};

/** Een regel uit het klantlogboek; labels komen uit de backend. */
export interface KaartLogregel {
  id: string;
  soort: string;
  soort_label: string;
  tekst: string;
  tijd: string;
  kanaal: string | null;
  kanaal_label: string | null;
  /** email | notitie | gesprek | belofte | zaak | chat | motor | handmatig */
  bron_soort: string;
  bron_id: string | null;
  zaak_id: string | null;
  door: string;
  door_label: string;
  /** Toezegging: open | nagekomen | vervallen. Afspraak: geldt | vervallen. */
  status: string | null;
  termijn_op: string | null;
  over_tijd: boolean;
  huisdier_id: string | null;
  /** Uit een vulronde (geschiedenis), niet live geschreven. */
  uit_geschiedenis: boolean;
}

export interface KaartLogboek {
  /** Alleen op de eerste pagina: afspraken die gelden en open toezeggingen. */
  bovenaan: KaartLogregel[];
  /** Nieuwste eerst. */
  regels: KaartLogregel[];
  heeft_meer: boolean;
  totaal: number;
}

/** Zoveel logboekregels per pagina. */
export const LOGBOEK_LIMIET = 30;

/** Hoogste limiet van `/klanten/{id}/gesprekken` (MAX_LIMIET in de backend). */
export const GESPREKKEN_LIMIET = 100;

export interface KaartGesprek {
  kanaal: string;
  bron_id: string;
  moment: string;
  titel: string;
  /** Sessie 141-E: wie het laatst namens ons antwoordde; leeg = onbekend. */
  door?: string | null;
  /** Sessie 141-E: de zaak van dit gesprek (`/?zaak=<id>`); leeg = geen. */
  zaak_id?: string | null;
}

/** Waar de volgende pagina van het logboek begint: na deze regel. */
export interface LogboekCursor {
  tijd: string;
  id: string;
}

export const kaart10Api = {
  /** Contacten voor "Eerdere contacten" en de tabteller: zoveel als de route geeft. */
  async gesprekken(klantId: string): Promise<KaartGesprek[]> {
    const { data } = await apiClient.get<KaartGesprek[]>(
      `/api/werkplek/klanten/${klantId}/gesprekken`,
      { params: { limiet: GESPREKKEN_LIMIET } },
    );
    return data;
  },
  async bestellingen(klantId: string): Promise<KaartBestellingen> {
    const { data } = await apiClient.get<KaartBestellingen>(
      `/api/werkplek/klanten/${klantId}/kaart/bestellingen`,
    );
    return data;
  },
  /** Bij het openen van een bestelling; de backend start zo nodig de live vraag. */
  async pakketten(klantId: string, orderId: string, herhaal = false): Promise<KaartPakketten> {
    // `herhaal`: een herhaalvraag tijdens het ophalen; die schrijft geen inzage-regel.
    const { data } = await apiClient.get<KaartPakketten>(
      `/api/werkplek/klanten/${klantId}/kaart/bestellingen/${orderId}/pakketten`,
      herhaal ? { params: { herhaal: true } } : undefined,
    );
    return data;
  },
  async retouren(klantId: string): Promise<KaartRetouren> {
    const { data } = await apiClient.get<KaartRetouren>(
      `/api/werkplek/klanten/${klantId}/kaart/retouren`,
    );
    return data;
  },
  /** Het klantlogboek; verder bladeren vanaf de laatste regel (`tijd` plus `id`). */
  async logboek(klantId: string, voor?: LogboekCursor | null): Promise<KaartLogboek> {
    const { data } = await apiClient.get<KaartLogboek>(
      `/api/werkplek/klanten/${klantId}/kaart/logboek`,
      {
        params: {
          limiet: LOGBOEK_LIMIET,
          ...(voor ? { voor: voor.tijd, voor_id: voor.id } : {}),
        },
      },
    );
    return data;
  },
};
